import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Button, Card, Collapse, DatePicker, Space, Typography, message } from 'antd';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import dayjs, { Dayjs } from 'dayjs';
import {
  DownloadOutlined,
  LineChartOutlined,
  AccountBookOutlined,
  UserOutlined,
} from '@ant-design/icons';
import axios from 'axios';
import Api from '../../Network';
import MobileTable from '../../Component/MobileTable';
import NameByLang from '../../Component/nameByLang';
import './SalesAnalyticsTab.scss';

const { RangePicker } = DatePicker;
const { Text, Title } = Typography;

export interface SalesAnalyticsTabProps {
  enabled: boolean;
  /** When set, analytics are limited to this sales representative (driver id). */
  driverId: string;
  driverName?: string;
  /** When set to sales_analytics, APIs require the dedicated report page permission. */
  permissionContext?: 'sales_analytics';
  /** When false, only download controls are shown (download-only permission). */
  canViewContent?: boolean;
  /** When false, the download button is hidden. Defaults to true when omitted. */
  canDownload?: boolean;
}

interface AnalyticsRow {
  sales_representative_id: number;
  sales_representative_name: string;
  product_id: number;
  product_name_en: string | null;
  product_name_ar: string | null;
  quantity_sold: number;
  avg_price_including_tax: number;
}

interface SalesAnalyticsData {
  date_range: { start_date: string; end_date: string };
  rows: AnalyticsRow[];
  total_sales_in_period?: number;
  total_receivables_for_customers?: number | null;
}

interface ExportErrorPayload {
  message?: string;
  error?: string;
}

const SalesAnalyticsTab: React.FC<SalesAnalyticsTabProps> = ({
  enabled,
  driverId,
  driverName,
  permissionContext,
  canViewContent = true,
  canDownload = true,
}) => {
  const { t, i18n } = useTranslation();
  /** What the user sees in the range picker */
  const [rangeDraft, setRangeDraft] = useState<[Dayjs, Dayjs] | null>(null);
  /** Committed range sent to the API; null = backend default (start of current month → today) */
  const [rangeApplied, setRangeApplied] = useState<[Dayjs, Dayjs] | null>(null);
  const syncedDraftFromResponse = useRef(false);

  const params = useMemo(() => {
    const base: Record<string, string> = {
      driver_id: driverId,
    };
    if (rangeApplied) {
      base.start_date = rangeApplied[0].format('YYYY-MM-DD');
      base.end_date = rangeApplied[1].format('YYYY-MM-DD');
    }
    if (permissionContext) {
      base.permission_context = permissionContext;
    }
    return base;
  }, [rangeApplied, driverId, permissionContext]);

  const queryRangeKey = rangeApplied
    ? `${rangeApplied[0].format('YYYY-MM-DD')}_${rangeApplied[1].format('YYYY-MM-DD')}`
    : 'default';

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['sales-analytics-products', driverId, queryRangeKey],
    queryFn: async (): Promise<SalesAnalyticsData> => {
      const res = await Api.get<{ status?: boolean; data?: SalesAnalyticsData }>(
        'api/admin/dashboard/sales-analytics-products',
        { params }
      );
      if (!res?.status || !res.data) {
        throw new Error('Failed to load sales analytics');
      }
      return res.data;
    },
    enabled: enabled && !!driverId && canViewContent,
  });

  useEffect(() => {
    if (!data?.date_range || syncedDraftFromResponse.current) {
      return;
    }
    setRangeDraft([dayjs(data.date_range.start_date), dayjs(data.date_range.end_date)]);
    syncedDraftFromResponse.current = true;
  }, [data?.date_range]);

  useEffect(() => {
    if (!enabled) {
      syncedDraftFromResponse.current = false;
      setRangeDraft(null);
      setRangeApplied(null);
    }
  }, [enabled]);

  const handleApplyRange = () => {
    if (rangeDraft?.[0] && rangeDraft[1]) {
      setRangeApplied([rangeDraft[0], rangeDraft[1]]);
    } else {
      setRangeApplied(null);
    }
  };

  const handleDownload = () => {
    if (!canDownload) {
      message.error(
        t(
          'salesRepresentatives.salesAnalytics.downloadForbidden',
          'You do not have permission to download this report.'
        )
      );
      return;
    }

    const dr =
      data?.date_range ??
      (rangeApplied
        ? {
            start_date: rangeApplied[0].format('YYYY-MM-DD'),
            end_date: rangeApplied[1].format('YYYY-MM-DD'),
          }
        : rangeDraft?.[0] && rangeDraft[1]
          ? {
              start_date: rangeDraft[0].format('YYYY-MM-DD'),
              end_date: rangeDraft[1].format('YYYY-MM-DD'),
            }
          : null);

    if (!dr) {
      message.warning(
        t('salesRepresentatives.salesAnalytics.applyHint', 'Choose dates and click Apply first')
      );
      return;
    }

    if (canViewContent && (!data || (data.rows?.length ?? 0) === 0)) {
      message.warning(
        t('salesRepresentatives.salesAnalytics.downloadNoData', 'No data to download')
      );
      return;
    }
    const token = localStorage.getItem('token');
    const baseURL = process.env.REACT_APP_API_URL || 'https://driveshield.net';
    const apiBaseURL = baseURL.startsWith('http') ? baseURL : `http://${baseURL}`;

    axios
      .get(`${apiBaseURL}/api/admin/dashboard/export-sales-analytics-products`, {
        params: {
          driver_id: driverId,
          start_date: dr.start_date,
          end_date: dr.end_date,
          ...(permissionContext ? { permission_context: permissionContext } : {}),
        },
        responseType: 'blob',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      })
      .then(async (response) => {
        if (!(response.data instanceof Blob)) {
          throw new TypeError('Invalid response format from export API');
        }

        const contentType = response.headers['content-type'] || '';
        if (contentType.includes('application/json')) {
          const text = await response.data.text();
          const errorData = JSON.parse(text) as ExportErrorPayload;
          throw new Error(errorData.message || errorData.error || 'Export failed');
        }

        const blob = new Blob([response.data], {
          type: 'application/pdf',
        });
        const url = globalThis.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `sales_analytics_${driverId}_${dr.start_date}_${dr.end_date}.pdf`;
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        link.remove();
        globalThis.URL.revokeObjectURL(url);
        message.success(
          t('salesRepresentatives.salesAnalytics.downloadSuccess', 'Report downloaded successfully')
        );
      })
      .catch(async (error: unknown) => {
        const requestError = error as {
          message?: string;
          response?: { data?: Blob };
        };

        if (requestError.response?.data instanceof Blob) {
          try {
            const errorText = await requestError.response.data.text();
            const errorData = JSON.parse(errorText) as ExportErrorPayload;
            message.error(
              errorData.message ||
                errorData.error ||
                t('salesRepresentatives.salesAnalytics.downloadError', 'Failed to download report')
            );
            return;
          } catch {
            // fall through to generic message
          }
        }

        message.error(
          requestError.message ||
            t('salesRepresentatives.salesAnalytics.downloadError', 'Failed to download report')
        );
      });
  };

  const grouped = useMemo(() => {
    const rows = data?.rows ?? [];
    const map = new Map<number, { name: string; items: AnalyticsRow[] }>();
    rows.forEach((r) => {
      const g = map.get(r.sales_representative_id) ?? {
        name: r.sales_representative_name,
        items: [],
      };
      g.items.push(r);
      g.name = r.sales_representative_name;
      map.set(r.sales_representative_id, g);
    });
    return Array.from(map.entries()).sort((a, b) => a[1].name.localeCompare(b[1].name));
  }, [data?.rows]);

  const productColumns = [
    {
      title: t('salesRepresentatives.salesAnalytics.columns.product', 'Product'),
      key: 'product',
      render: (_: unknown, r: AnalyticsRow) => (
        <NameByLang
          name_en={r.product_name_en ?? undefined}
          name_ar={r.product_name_ar ?? undefined}
        />
      ),
    },
    {
      title: t('salesRepresentatives.salesAnalytics.columns.quantity', 'Quantity sold'),
      dataIndex: 'quantity_sold',
      key: 'quantity_sold',
      align: 'right' as const,
      render: (v: number) => Number(v).toLocaleString(i18n.language),
    },
    {
      title: t('salesRepresentatives.salesAnalytics.columns.avgPriceTax', 'Avg. price (incl. tax)'),
      dataIndex: 'avg_price_including_tax',
      key: 'avg_price_including_tax',
      align: 'right' as const,
      render: (v: number) => `${Number(v).toLocaleString(i18n.language)} ${t('common.SAR', 'SAR')}`,
    },
  ];

  const sar = t('common.SAR', 'SAR');

  const formatMoney = (n: number) =>
    Number(n).toLocaleString(i18n.language, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });

  return (
    <div className="sales-analytics-tab">
      <Card className="sales-analytics-tab__filters-card">
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, alignItems: 'center' }}>
            <Text strong>{t('salesRepresentatives.salesOverview.dateRange', 'Date range')}</Text>
            <RangePicker
              value={rangeDraft}
              onChange={(dates) => {
                if (dates?.[0] && dates[1]) {
                  setRangeDraft([dates[0], dates[1]]);
                } else {
                  setRangeDraft(null);
                }
              }}
              allowClear
              style={{ minWidth: 280 }}
            />
            <Button type="primary" onClick={handleApplyRange}>
              {t('salesRepresentatives.salesAnalytics.applyDateRange', 'Apply range')}
            </Button>
            {canDownload && (
              <Button icon={<DownloadOutlined />} onClick={handleDownload}>
                {t('salesRepresentatives.salesAnalytics.downloadResults', 'Download results')}
              </Button>
            )}
            <Text type="secondary">
              {t(
                'salesRepresentatives.salesAnalytics.applyHint',
                'Choose dates and click Apply to refresh. Clear dates and Apply to use the default range.'
              )}
            </Text>
          </div>
        </Space>
      </Card>

      {canViewContent && (
        <Card className="sales-analytics-tab__main-card" loading={isLoading || isFetching}>
          <div className="sales-analytics-tab__hero">
            <Title level={4} className="sales-analytics-tab__title">
              {t('salesRepresentatives.salesAnalytics.title', 'Sales analytics by product')}
            </Title>
            {driverName ? (
              <div className="sales-analytics-tab__rep">
                <UserOutlined aria-hidden />
                <NameByLang name={driverName} className="bold" />
              </div>
            ) : null}
          </div>

          {data?.total_sales_in_period != null && (
            <div className="sales-analytics-tab__metrics">
              <div className="sales-analytics-tab__metric sales-analytics-tab__metric--sales">
                <div className="sales-analytics-tab__metric-icon">
                  <LineChartOutlined aria-hidden />
                </div>
                <div className="sales-analytics-tab__metric-body">
                  <div className="sales-analytics-tab__metric-label">
                    {t(
                      'salesRepresentatives.salesAnalytics.totalSalesInPeriod',
                      'Total sales (selected period)'
                    )}
                  </div>
                  <div>
                    <span className="sales-analytics-tab__metric-value">
                      {formatMoney(data.total_sales_in_period)}
                    </span>
                    <span className="sales-analytics-tab__metric-suffix">{sar}</span>
                  </div>
                  <div className="sales-analytics-tab__metric-hint">
                    {t(
                      'salesRepresentatives.salesAnalytics.totalSalesInPeriodHint',
                      'Approved orders for this representative, including tax, within the selected range.'
                    )}
                  </div>
                </div>
              </div>
              {data.total_receivables_for_customers != null && (
                <div className="sales-analytics-tab__metric sales-analytics-tab__metric--receivables">
                  <div className="sales-analytics-tab__metric-icon">
                    <AccountBookOutlined aria-hidden />
                  </div>
                  <div className="sales-analytics-tab__metric-body">
                    <div className="sales-analytics-tab__metric-label">
                      {t(
                        'salesRepresentatives.salesAnalytics.totalReceivablesAllCustomers',
                        'Total receivables (linked customers)'
                      )}
                    </div>
                    <div>
                      <span className="sales-analytics-tab__metric-value">
                        {formatMoney(data.total_receivables_for_customers)}
                      </span>
                      <span className="sales-analytics-tab__metric-suffix">{sar}</span>
                    </div>
                    <div className="sales-analytics-tab__metric-hint">
                      {t(
                        'salesRepresentatives.salesAnalytics.totalReceivablesHint',
                        'Sum of invoice due amounts for all customers linked to this representative (not limited by the date range).'
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="sales-analytics-tab__table-wrap">
            {grouped.length === 0 && !isLoading && !isFetching ? (
              <Text type="secondary">{t('common.noData', 'No data')}</Text>
            ) : grouped.length === 1 ? (
              <MobileTable
                rowKey={(r) => `${r.sales_representative_id}-${r.product_id}`}
                columns={productColumns}
                dataSource={grouped[0][1].items}
                pagination={{ pageSize: 20, showSizeChanger: true }}
                size="small"
                scroll={{ x: 600 }}
              />
            ) : (
              <Collapse
                items={grouped.map(([repId, { name, items }]) => ({
                  key: String(repId),
                  label: (
                    <Space>
                      <Text strong>
                        <NameByLang name={name} className="bold" />
                      </Text>
                      <Text type="secondary">
                        ({items.length}{' '}
                        {t('salesRepresentatives.salesAnalytics.products', 'products')})
                      </Text>
                    </Space>
                  ),
                  children: (
                    <MobileTable
                      rowKey={(r) => `${repId}-${r.product_id}`}
                      columns={productColumns}
                      dataSource={items}
                      pagination={false}
                      size="small"
                      scroll={{ x: 600 }}
                    />
                  ),
                }))}
              />
            )}
          </div>
        </Card>
      )}
    </div>
  );
};

export default SalesAnalyticsTab;
